import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { JobDetailDrawer } from "./JobDetailDrawer";
import { NewJobDialog } from "./NewJobDialog";

// Beast V1 Checkpoint 4 — Jobs list panel.
// Columns: Customer · Job · Received · Hours · Work · Billing.
// Minimal filters (work_status, billing_status, customer, text). Row
// click opens JobDetailDrawer.
const WORK_LABELS = {
  new: "New",
  in_progress: "In Progress",
  waiting_on_client: "Waiting on Client",
  complete: "Complete",
  cancelled: "Cancelled",
};

const BILLING_LABELS = {
  not_ready: "Not Ready",
  ready_to_bill: "Ready to Bill",
  invoice_sent: "Invoice Sent",
  partially_paid: "Partially Paid",
  paid: "Paid",
  no_charge: "No Charge",
};

const commercialLabel = (mode) => {
  if (mode === "managed") return "MANAGED";
  if (mode === "retainer") return "RETAINER";
  return "STANDARD";
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso.slice(0, 10);
  }
};

export const JobsPanel = ({
  compact = false,
  focusJobId = null,
  onFocusHandled,
}) => {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterWork, setFilterWork] = useState("");
  const [filterBilling, setFilterBilling] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showNewJob, setShowNewJob] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [jr, cr] = await Promise.all([
        api.get("/admin/jobs"),
        api.get("/admin/customers"),
      ]);
      setJobs(Array.isArray(jr.data) ? jr.data : []);
      const map = {};
      (Array.isArray(cr.data) ? cr.data : []).forEach((c) => {
        map[c.id] = c;
      });
      setCustomers(map);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Support external "open this job" requests (e.g., from Email
  // Intake's "View Job" action).
  useEffect(() => {
    if (focusJobId) {
      setSelectedId(focusJobId);
      onFocusHandled?.();
    }
  }, [focusJobId, onFocusHandled]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobs.filter((j) => {
      if (filterWork && j.work_status !== filterWork) return false;
      if (filterBilling && j.billing_status !== filterBilling) return false;
      if (filterCustomer && j.customer_id !== filterCustomer) return false;
      if (q) {
        const custName = (customers[j.customer_id]?.display_name || "").toLowerCase();
        const title = (j.title || "").toLowerCase();
        if (!custName.includes(q) && !title.includes(q)) return false;
      }
      return true;
    });
  }, [jobs, filterWork, filterBilling, filterCustomer, search, customers]);

  const visible = compact ? filtered.slice(0, 8) : filtered;
  const customerList = useMemo(() => Object.values(customers), [customers]);

  return (
    <section data-testid="jobs-panel" className="ur-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="ur-eyebrow">Jobs</div>
        <button
          data-testid="new-job-btn"
          onClick={() => setShowNewJob(true)}
          className="ur-btn-primary text-xs py-2 px-4"
        >
          <Plus className="w-3.5 h-3.5" /> New Job
        </button>
      </div>

      {!compact && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#5A5A5A] absolute left-2.5 top-2.5" />
            <input
              data-testid="jobs-search"
              className="ur-input pl-8 text-sm min-w-[220px]"
              placeholder="Search customer or title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            data-testid="jobs-filter-work"
            className="ur-input text-sm"
            value={filterWork}
            onChange={(e) => setFilterWork(e.target.value)}
          >
            <option value="">All Work Statuses</option>
            {Object.entries(WORK_LABELS).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          <select
            data-testid="jobs-filter-billing"
            className="ur-input text-sm"
            value={filterBilling}
            onChange={(e) => setFilterBilling(e.target.value)}
          >
            <option value="">All Billing Statuses</option>
            {Object.entries(BILLING_LABELS).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
          <select
            data-testid="jobs-filter-customer"
            className="ur-input text-sm"
            value={filterCustomer}
            onChange={(e) => setFilterCustomer(e.target.value)}
          >
            <option value="">All Customers</option>
            {customerList
              .sort((a, b) => (a.display_name || "").localeCompare(b.display_name || ""))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name}
                </option>
              ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[#A3A3A3] py-6 text-center">Loading…</div>
      ) : jobs.length === 0 ? (
        <div className="text-sm text-[#A3A3A3] py-6 text-center" data-testid="jobs-empty">
          No jobs yet. Start with <span className="text-white font-semibold">+ New Job</span>.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm" data-testid="jobs-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#5A5A5A]">
                <th className="text-left px-2 py-2">Customer</th>
                <th className="text-left px-2 py-2">Job</th>
                <th className="text-left px-2 py-2">Received</th>
                <th className="text-left px-2 py-2">Hours</th>
                <th className="text-left px-2 py-2">Work</th>
                <th className="text-left px-2 py-2">Billing</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((j) => (
                <tr
                  key={j.id}
                  data-testid={`jobs-row-${j.id}`}
                  onClick={() => setSelectedId(j.id)}
                  className="border-t border-[#1F1F1F] hover:bg-[#161616] cursor-pointer"
                >
                  <td className="px-2 py-2 align-top">
                    <div className="font-medium text-white truncate max-w-[180px]">
                      {customers[j.customer_id]?.display_name || "—"}
                    </div>
                    <div className="text-[10px] text-[#FF6600] font-bold tracking-widest uppercase">
                      {commercialLabel(j.commercial_mode_snapshot)}
                    </div>
                  </td>
                  <td className="px-2 py-2 align-top">
                    <div className="text-white truncate max-w-[280px]">{j.title || "(untitled)"}</div>
                  </td>
                  <td className="px-2 py-2 align-top text-[#A3A3A3]">{formatDate(j.received_at)}</td>
                  <td className="px-2 py-2 align-top">{(j.total_hours ?? 0).toFixed(2)}</td>
                  <td className="px-2 py-2 align-top">{WORK_LABELS[j.work_status] || j.work_status}</td>
                  <td className="px-2 py-2 align-top">
                    {BILLING_LABELS[j.billing_status] || j.billing_status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {compact && filtered.length > visible.length && (
            <div className="text-xs text-[#A3A3A3] mt-3 px-2">
              Showing {visible.length} of {filtered.length}. Open <span className="text-white">Operations → Jobs</span> for the full list.
            </div>
          )}
        </div>
      )}

      {selectedId && (
        <JobDetailDrawer
          jobId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}
      {showNewJob && (
        <NewJobDialog
          onClose={() => setShowNewJob(false)}
          onCreated={(job) => {
            setShowNewJob(false);
            load();
            setSelectedId(job.id);
          }}
        />
      )}
    </section>
  );
};
